import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Upload, FileText, Image as ImageIcon, X, ArrowLeft, Plus, Search,
  DollarSign, Calendar, Building2, Hash, FolderOpen, Receipt,
  CheckCircle2, AlertTriangle, ExternalLink, Eye, MoreHorizontal,
  Check, Send, Ban, RotateCcw, AlertCircle, Mail, User as UserIcon,
  Zap, Download, Copy, Banknote,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import BudgetPoPanel from "./BudgetPoPanel";   // 28 Oct 2026 — PO + bucket with what is left
import { xeroTransactionUrl } from "@/lib/xero";
import { splitFromInc } from "@/lib/gst";   // T-403
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Input, Select, Spinner, EmptyState, KPI,
} from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import { exportCsv, dated } from "@/lib/exportCsv";

// =============================================================================
// Shared helpers
// =============================================================================

const STATUS_TONE: Record<string, any> = {
  captured:        "pending",
  approved:        "mobilising",
  entered_in_xero: "onsite",
  paid:            "won",
  disputed:        "lost",
  void:            "pending",
};

const STATUS_LABEL: Record<string, string> = {
  captured:        "Captured",
  approved:        "Approved",
  entered_in_xero: "In Xero",
  paid:            "Paid",
  disputed:        "Disputed",
  void:            "Void",
};

function statusPill(status: string) {
  return (
    <Pill
      label={STATUS_LABEL[status] ?? status}
      tone={STATUS_TONE[status] ?? "pending"}
      small
    />
  );
}

// =============================================================================
// ApCaptureScreen — /ap/new
// =============================================================================
// Open to any authenticated user so field staff + office can both capture
// invoices into the central queue. Manager approval happens later in the
// queue drawer, not here.
// =============================================================================

export function ApCaptureScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // T-033 Commit B — if we arrived here from the Pending receipts queue,
  // load the source receipt + pre-fill supplier/amount/notes + mark the
  // pending_receipt as promoted after successful save.
  const [searchParams] = useSearchParams();
  const pendingReceiptId = searchParams.get("pending_receipt_id");

  // File upload
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form fields
  const [supplierId, setSupplierId] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [vendorName, setVendorName] = useState("");  // T-318: one-off card-receipt payee (no supplier account needed)
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  const [newSupplierOpen, setNewSupplierOpen] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState("");
  const [newSupplierAbn, setNewSupplierAbn] = useState("");
  const [newSupplierTerms, setNewSupplierTerms] = useState("30");

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [amountExGst, setAmountExGst] = useState("");
  const [gstAmount, setGstAmount] = useState("");
  const [gstManual, setGstManual] = useState(false);
  const [totalInput, setTotalInput] = useState("");  // T-318: receipt total (inc GST) — primary entry, ex/GST auto-split
  const [gstFree, setGstFree] = useState(false);      // T-318: GST-free override (no GST on this receipt)
  const [docType, setDocType] = useState<"receipt" | "bill" | "reimbursement">("bill");  // T-331 visual flag

  // Invoice type drives budget code filtering + whether a project is required
  //   'project' → PX* codes, project REQUIRED
  //   'opex'    → OX* codes, no project
  //   'capex'   → CX* codes, no project
  type InvoiceType = "project" | "opex" | "capex";
  const [invoiceType, setInvoiceType] = useState<InvoiceType>("project");

  const [budgetCode, setBudgetCode] = useState("");
  const [projectId, setProjectId] = useState("");
  const [poId, setPoId] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");

  // -------- Data loads -----------------------------------------------------

  const { data: suppliers = [] } = useQuery({
    queryKey: ["ap_suppliers"],
    queryFn: async () => {
      const { data } = await supabase
        .from("suppliers")
        .select("supplier_id, legal_name, trading_name, abn, payment_terms_days")
        .eq("is_active", true)
        .order("legal_name");
      return (data ?? []) as any[];
    },
  });

  const { data: budgetCodes = [], error: budgetCodesError } = useQuery({
    queryKey: ["ap_budget_codes"],
    queryFn: async () => {
      // Lean select — avoid referencing columns that may not exist.
      // If finance_budget_codes is missing or empty, we show a clear message
      // below the dropdown rather than silently failing.
      const { data, error } = await supabase
        .from("budget_codes")
        .select("budget_code, description:display_name, category")
        .eq("is_active", true)  // T-331 — the canonical Xero budget codes
        .order("sort_order");
      if (error) {
        console.error("[AP] budget codes query failed:", error);
        throw error;
      }
      return (data ?? []) as any[];
    },
  });

  const { data: projects = [], error: projectsError } = useQuery({
    queryKey: ["ap_projects"],
    queryFn: async () => {
      // Active projects only — closed/cancelled projects don't accept new
      // AP entries (T-086 Phase A). Trailing invoices for closed projects
      // are handled by a manager reopening the specific budget code.
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, site:site_id ( site_name )")
        .not("status", "in", "(closed,cancelled)")
        .order("project_code", { ascending: false })
        .limit(200);
      if (error) {
        console.error("[AP] projects query failed:", error);
        throw error;
      }
      return (data ?? []) as any[];
    },
  });

  // Only fetch POs once we know both project + supplier, otherwise the PO list
  // is meaningless (we'd want "open POs for this supplier on this project")
  const { data: pos = [] } = useQuery({
    queryKey: ["ap_pos", projectId, supplierId],
    enabled: !!projectId && !!supplierId,
    queryFn: async () => {
      const { data } = await supabase
        .from("purchase_orders")
        .select("po_id, po_number, subtotal_ex_gst, status")   // T-397 — total_ex_gst never existed (PO dropdown was empty)
        .eq("project_id", projectId)
        .eq("supplier_id", supplierId)
        .order("po_number", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  // T-033 Commit B — pending_receipt source when coming from /ap/receipts
  const { data: pendingReceipt } = useQuery({
    queryKey: ["ap_capture_pending_receipt", pendingReceiptId],
    enabled: !!pendingReceiptId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pending_receipts")
        .select(`
          receipt_id, captured_at, storage_path, mime_type,
          supplier_guess, amount_guess, notes, project_id,
          captured_by_employee_id, status,
          capturer:employees!pending_receipts_captured_by_employee_id_fkey(first_name, last_name)
        `)
        .eq("receipt_id", pendingReceiptId!)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  const { data: pendingReceiptUrl } = useQuery({
    queryKey: ["ap_capture_pending_receipt_url", pendingReceiptId],
    enabled: !!pendingReceipt?.storage_path,
    queryFn: async () => {
      const { data, error } = await supabase
        .storage
        .from("receipt-captures")
        .createSignedUrl(pendingReceipt!.storage_path, 3600);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });

  // Pre-fill the form once from the pending_receipt. Runs once per receipt
  // load — we guard with a ref so director edits aren't clobbered.
  const preFilledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingReceipt || preFilledRef.current === pendingReceipt.receipt_id) return;
    preFilledRef.current = pendingReceipt.receipt_id;

    if (pendingReceipt.supplier_guess) setSupplierSearch(pendingReceipt.supplier_guess);
    if (pendingReceipt.notes) setNotes(pendingReceipt.notes);
    if (pendingReceipt.project_id) {
      setProjectId(pendingReceipt.project_id);
      setInvoiceType("project");
    }
    if (pendingReceipt.amount_guess != null) {
      // amount_guess from the mobile form is inc-GST. Split at the standard
      // 10% (can be corrected manually in the form).
      const inc = Number(pendingReceipt.amount_guess);
      const { ex, gst } = splitFromInc(inc);   // T-403 — shared lib/gst split
      setTotalInput(inc.toString());
      setAmountExGst(ex.toString());
      setGstAmount(gst.toString());
    }
  }, [pendingReceipt]);

  // When a project is selected, fetch its budget_lines to know which codes
  // are actually valid for that project. You can only spend against budgets
  // that were planned — if the accepted quote didn't include a Steelwork
  // line, this project shouldn't accept a PX05_STEEL invoice.
  const { data: projectBudgetCodes = [] } = useQuery({
    queryKey: ["project_budget_codes", projectId],
    enabled: invoiceType === "project" && !!projectId,
    queryFn: async () => {
      // Only active lines — closed projects' lines are hidden from new AP
      // entry per T-086 Phase A. If a real trailing invoice arrives the
      // manager reopens the specific code in the project budget admin.
      const { data, error } = await supabase
        .from("budget_lines")
        .select("budget_code")
        .eq("project_id", projectId)
        .eq("is_active", true);
      if (error) { console.error("[AP] project budget codes query failed:", error); throw error; }
      // Dedupe — a project can have multiple lines under the same code
      return Array.from(new Set(((data ?? []) as any[]).map((r) => r.budget_code).filter(Boolean)));
    },
  });

  // -------- Derived ---------------------------------------------------------

  const selectedSupplier = suppliers.find((s: any) => s.supplier_id === supplierId);

  // T-296 / T-318 — brand-collapse preview for the selected supplier OR a typed
  // one-off vendor name. Tags brand_id on submit + shows a coding suggestion.
  const effVendorName = selectedSupplier?.legal_name || vendorName.trim();
  const { data: brandMatch } = useQuery({
    queryKey: ["ap_brand_match", supplierId, effVendorName],
    enabled: !!effVendorName,
    queryFn: async () => {
      const { data: bm } = await supabase.schema("app").rpc("collapse_supplier", {
        p_legal_name: effVendorName || null,
        p_abn: selectedSupplier?.abn ?? null,
      });
      const hit = (bm as any[])?.[0];
      if (!hit?.brand_id) return null;
      const [{ data: brand }, { data: coding }] = await Promise.all([
        supabase.from("brand_map").select("brand_display_name").eq("brand_id", hit.brand_id).maybeSingle(),
        supabase.from("coding_defaults").select("suggested_xero_account, suggested_tax_rate, suggested_budget_code, suggested_ftc_class").eq("brand_id", hit.brand_id).maybeSingle(),
      ]);
      return {
        brand_id: hit.brand_id as string,
        confidence: hit.confidence as number,
        matched_via: hit.matched_via as string,
        brand_display_name: (brand as any)?.brand_display_name ?? "brand",
        coding: coding as any,
      };
    },
  });

  // T-331 — card register + apply brand coding on the manual / AI-can't-read path.
  const { data: apCards = [] } = useQuery({
    queryKey: ["ap_cards"],
    queryFn: async () => {
      const { data } = await supabase.from("card_register")
        .select("card_id, last4, label, card_type, holder_employee_id, xero_account_name")
        .eq("is_active", true).order("last4");
      return (data ?? []) as any[];
    },
  });
  const [cardId, setCardId] = useState("");
  const [ftcClass, setFtcClass] = useState("");
  const [manualFuel, setManualFuel] = useState(false);
  const apSelCard = (apCards as any[]).find((c) => c.card_id === cardId) ?? null;
  const apCardPaymentMethod = apSelCard
    ? (apSelCard.card_type === "company_credit" ? "card_credit"
       : apSelCard.card_type === "company_debit" ? "card_debit" : "reimbursement")
    : null;
  const apCardIsReimb = apSelCard?.card_type === "employee_personal";
  useEffect(() => {
    if (!brandMatch?.coding) return;
    if (!budgetCode && brandMatch.coding.suggested_budget_code) setBudgetCode(brandMatch.coding.suggested_budget_code);
    if (!ftcClass && brandMatch.coding.suggested_ftc_class) setFtcClass(brandMatch.coding.suggested_ftc_class);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandMatch]);

  const filteredSuppliers = useMemo(() => {
    const q = supplierSearch.trim().toLowerCase();
    if (!q) return suppliers.slice(0, 50);
    return suppliers.filter((s: any) =>
      s.legal_name?.toLowerCase().includes(q) ||
      s.trading_name?.toLowerCase().includes(q) ||
      s.abn?.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [suppliers, supplierSearch]);

  // Budget codes filtered to the currently-selected invoice type.
  // Prefix convention:  PX* = project, OX* = opex (overhead), CX* = capex.
  //
  // Additionally, when invoiceType='project' AND a specific project is picked,
  // we narrow further to ONLY the budget_codes that have budget_line rows for
  // that project. Prevents attributing invoices to codes the project didn't
  // plan against (keeps project P&L clean).
  const filteredBudgetCodes = useMemo(() => {
    const prefix =
      invoiceType === "project" ? "PX" :
      invoiceType === "opex"    ? "OX" :
      invoiceType === "capex"   ? "CX" : "";

    let codes = budgetCodes.filter((b: any) =>
      (b.budget_code ?? "").toUpperCase().startsWith(prefix)
    );

    // Narrow by project's actual budget lines, if applicable
    if (invoiceType === "project" && projectId && projectBudgetCodes.length > 0) {
      const allowed = new Set(projectBudgetCodes);
      codes = codes.filter((b: any) => allowed.has(b.budget_code));
    }

    return codes;
  }, [budgetCodes, invoiceType, projectId, projectBudgetCodes]);

  // Flag: project selected but has zero budget lines (usually: no accepted quote yet)
  const projectHasNoBudget =
    invoiceType === "project" &&
    !!projectId &&
    projectBudgetCodes.length === 0;

  // When invoice type changes, clear selections that no longer make sense
  useEffect(() => {
    // Clear budget code if it doesn't fit the new type
    const prefix =
      invoiceType === "project" ? "PX" :
      invoiceType === "opex"    ? "OX" :
      invoiceType === "capex"   ? "CX" : "";
    if (budgetCode && !budgetCode.toUpperCase().startsWith(prefix)) {
      setBudgetCode("");
    }
    // OPEX / CAPEX don't link to a project
    if (invoiceType !== "project") {
      setProjectId("");
      setPoId("");
    }
  }, [invoiceType]); // eslint-disable-line

  // Also clear the budget code when the project changes — different projects
  // can have different budget lines, so a previously-valid code may now be invalid
  useEffect(() => {
    if (invoiceType === "project" && budgetCode && projectBudgetCodes.length > 0) {
      if (!projectBudgetCodes.includes(budgetCode)) {
        setBudgetCode("");
      }
    }
  }, [projectId, projectBudgetCodes]); // eslint-disable-line

  // Auto-set due date from supplier payment terms + invoice date
  const didAutoDueRef = useRef(false);
  useEffect(() => {
    if (!selectedSupplier || !invoiceDate) return;
    const terms = Number(selectedSupplier.payment_terms_days ?? 30);
    const d = new Date(invoiceDate);
    d.setDate(d.getDate() + terms);
    setDueDate(d.toISOString().slice(0, 10));
    didAutoDueRef.current = true;
  }, [supplierId, invoiceDate]);   // eslint-disable-line

  // Auto-calc GST at 10% unless user manually overrode
  useEffect(() => {
    if (gstManual) return;
    const ex = Number(amountExGst) || 0;
    setGstAmount(ex > 0 ? (ex * 0.10).toFixed(2) : "");
  }, [amountExGst, gstManual]);

  const amountIncGst = +((Number(amountExGst) || 0) + (Number(gstAmount) || 0)).toFixed(2);

  // Blob URL for local preview — works for both images and PDFs
  useEffect(() => {
    if (!file) {
      setFilePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // -------- Mutations -------------------------------------------------------

  const createSupplier = useMutation({
    mutationFn: async () => {
      if (!newSupplierName.trim()) throw new Error("Supplier name required");
      const { data, error } = await supabase
        .from("suppliers")
        .insert({
          legal_name: newSupplierName.trim(),
          abn: newSupplierAbn.trim() || null,
          payment_terms_days: Number(newSupplierTerms) || 30,
          is_active: true,
        })
        .select("supplier_id, legal_name, trading_name, abn, payment_terms_days")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["ap_suppliers"] });
      setSupplierId(data.supplier_id);
      setSupplierSearch("");
      setSupplierDropdownOpen(false);
      setNewSupplierOpen(false);
      setNewSupplierName("");
      setNewSupplierAbn("");
      setNewSupplierTerms("30");
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      if (!supplierId && !vendorName.trim()) throw new Error("Pick a supplier, or type a vendor name for a one-off receipt");
      if (!amountExGst || Number(amountExGst) <= 0) throw new Error("Enter the invoice amount ex GST");
      if (!budgetCode) throw new Error("Select a budget code");
      if (invoiceType === "project" && !projectId) {
        throw new Error("Project-type invoices must be linked to a project");
      }

      let pdfStoragePath: string | null = null;
      let pdfOriginalFilename: string | null = null;

      if (file) {
        const ym = new Date().toISOString().slice(0, 7);
        const safe = file.name.replace(/[^\w.\-]/g, "_");
        const key = `${ym}/${Date.now()}-${safe}`;
        const { error } = await supabase.storage
          .from("ap-documents")
          .upload(key, file, { cacheControl: "3600", upsert: false });
        if (error) throw new Error(`Upload failed: ${error.message}`);
        pdfStoragePath = key;
        pdfOriginalFilename = file.name;
      }

      const { data, error } = await supabase
        .from("ap_invoices")
        .insert({
          invoice_number:        invoiceNumber.trim() || null,
          supplier_id:           supplierId || null,
          supplier_name:         selectedSupplier?.legal_name ?? (vendorName.trim() || null),
          brand_id:              brandMatch?.brand_id ?? null,
          invoice_date:          invoiceDate || null,
          due_date:              dueDate || null,
          description:           description.trim() || null,
          notes:                 notes.trim() || null,
          amount_ex_gst:         Number(amountExGst),
          gst_amount:            Number(gstAmount) || 0,
          amount_inc_gst:        amountIncGst,
          budget_code:           budgetCode || null,
          ftc_class:             ftcClass || null,
          card_id:               cardId || null,
          card_last4:            apSelCard?.last4 ?? null,
          payment_method:        apCardPaymentMethod,
          is_reimbursement:      apCardIsReimb,
          reimburse_employee_id: apCardIsReimb ? (apSelCard?.holder_employee_id ?? null) : null,
          project_id:            projectId || null,
          po_id:                 poId || null,
          pdf_storage_path:      pdfStoragePath,
          pdf_original_filename: pdfOriginalFilename,
          captured_by:           (user as any).user_id ?? (user as any).id ?? null,
          capture_source:        pendingReceiptId ? "scan" : "manual",
          status:                "captured",
        })
        .select("ap_invoice_id")
        .single();

      if (error) throw error;

      // T-033 Commit B — if we came from the Pending receipts queue, mark
      // the source receipt as promoted and notify the capturer. Best-effort
      // so a notification failure doesn't unwind the AP invoice.
      if (pendingReceiptId && pendingReceipt) {
        const newApId = data.ap_invoice_id as string;

        const { error: promErr } = await supabase
          .from("pending_receipts")
          .update({
            status: "promoted",
            promoted_to_ap_invoice_id: newApId,
            promoted_at: new Date().toISOString(),
            promoted_by_user_id: (user as any).user_id ?? (user as any).id ?? null,
          })
          .eq("receipt_id", pendingReceiptId);
        if (promErr) console.warn("[AP] pending_receipt promotion failed:", promErr);

        // Notify capturer
        const { data: capturerUser } = await supabase
          .from("users")
          .select("user_id")
          .eq("employee_id", pendingReceipt.captured_by_employee_id)
          .limit(1)
          .maybeSingle();
        if (capturerUser?.user_id) {
          await supabase.from("notifications").insert({
            channel: "in_app",
            event_type: "receipt.promoted",
            recipient_user_id: capturerUser.user_id,
            subject: "Your receipt has been coded",
            body_plain: `Receipt (${pendingReceipt.supplier_guess ?? "no supplier"}, ${pendingReceipt.amount_guess ? `$${Number(pendingReceipt.amount_guess).toFixed(2)}` : "amount TBC"}) is now in AP as invoice ${newApId.slice(0, 8)}.`,
            payload: { receipt_id: pendingReceiptId, ap_invoice_id: newApId, action_url: "/receipts" },
            related_table: "pending_receipts",
            related_id: pendingReceiptId,
            priority: 4,
          }).then(() => null, (e: unknown) => console.warn("[AP] notify capturer failed:", e));
        }
      }

      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ap_invoices"] });
      qc.invalidateQueries({ queryKey: ["ap_kpi"] });
      navigate("/ap");
    },
  });

  // -------- Render ----------------------------------------------------------

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <button onClick={() => navigate("/ap")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-3 hover:text-text">
        <ArrowLeft size={14} /> Back to AP
      </button>
      <h1 className="text-2xl font-semibold tracking-tight mb-1">Code this spend</h1>
      <div className="text-text-muted text-[13px] mb-4">The AI reads it, the brand map codes it — you check and approve.</div>

      <Card>
        <div className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5 items-start">

          {/* LEFT — receipt + doc-type flag */}
          <div className="space-y-3 md:sticky md:top-4">
            {(pendingReceiptUrl || file) ? (
              (pendingReceipt?.mime_type === "application/pdf" || (file && file.type === "application/pdf")) ? (
                <button type="button" title="Open PDF" onClick={() => { const u = pendingReceiptUrl ?? (file ? URL.createObjectURL(file) : null); if (u) window.open(u, "_blank", "noopener"); }}
                  className="aspect-[3/4] w-full bg-ink border border-line rounded-lg flex flex-col items-center justify-center text-text-muted hover:border-accent transition-colors"><FileText size={40} /><span className="text-[11px] mt-2">Open PDF</span></button>
              ) : (
                <img src={pendingReceiptUrl ?? (file ? URL.createObjectURL(file) : "")} alt="Receipt" title="Click to open full size"
                  style={{ cursor: "zoom-in" }}
                  onClick={() => { const u = pendingReceiptUrl ?? (file ? URL.createObjectURL(file) : null); if (u) window.open(u, "_blank", "noopener"); }}
                  className="w-full rounded-lg border border-line object-cover max-h-72 hover:border-accent transition-colors" />
              )
            ) : (
              <button type="button" onClick={() => fileInputRef.current?.click()}
                className="w-full aspect-[3/4] border-2 border-dashed border-line rounded-lg flex flex-col items-center justify-center text-text-muted hover:border-accent transition-colors">
                <Upload size={28} /><div className="text-[12px] mt-2">Upload / take photo</div>
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <div className="flex gap-1.5 flex-wrap">
              {(["receipt", "bill", "reimbursement"] as const).map((t) => (
                <button key={t} type="button" onClick={() => setDocType(t)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border capitalize ${docType === t ? "bg-accent text-white border-accent" : "bg-ink-raised text-text-muted border-line"}`}>{t}</button>
              ))}
            </div>
            {pendingReceipt && <div className="text-[11px] text-text-muted">From a scanned receipt</div>}
          </div>

          {/* RIGHT — supplier, amount, coding */}
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <label className="block text-[11px] uppercase tracking-wider2 text-text-muted mb-1">Supplier</label>
                <div className="relative">
                  <Input
                    value={supplierId ? (selectedSupplier?.legal_name ?? "") : (vendorName || supplierSearch)}
                    onChange={(e) => { setSupplierId(""); setVendorName(e.target.value); setSupplierSearch(e.target.value); setSupplierDropdownOpen(true); }}
                    onFocus={() => setSupplierDropdownOpen(true)}
                    placeholder="Search supplier or type a one-off vendor"
                  />
                  {supplierDropdownOpen && supplierSearch.trim() && filteredSuppliers.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-ink-card border border-line rounded-md shadow-lg max-h-56 overflow-y-auto">
                      {filteredSuppliers.map((sp: any) => (
                        <button key={sp.supplier_id} type="button"
                          onClick={() => { setSupplierId(sp.supplier_id); setVendorName(""); setSupplierSearch(""); setSupplierDropdownOpen(false); }}
                          className="w-full text-left px-3 py-2 hover:bg-ink-raised text-[13px]">
                          {sp.legal_name}{sp.abn ? <span className="text-text-muted font-mono text-[11px]"> · {sp.abn}</span> : null}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="w-32">
                <label className="block text-[11px] uppercase tracking-wider2 text-text-muted mb-1">Total inc GST</label>
                <Input value={totalInput} placeholder="0.00"
                  onChange={(e) => { const v = e.target.value; setTotalInput(v); const t = Number(v) || 0; const s = splitFromInc(t, gstFree); setAmountExGst(t ? s.ex.toFixed(2) : ""); setGstAmount(t ? s.gst.toFixed(2) : gstFree ? "0" : ""); }} />
              </div>
            </div>

            <div className="flex items-center gap-3 text-[11px] text-text-muted flex-wrap">
              <span>ex {amountExGst || "0.00"}</span>
              <span>GST {gstAmount || "0.00"}</span>
              <label className="inline-flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={gstFree} onChange={(e) => { setGstFree(e.target.checked); if (e.target.checked) { setGstAmount("0"); setAmountExGst(totalInput || ""); } }} className="accent-accent" /> GST-free</label>
              <input className="ml-auto bg-ink border border-line rounded px-2 py-1 text-right text-[11px] text-text w-32" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="Their invoice #" />
            </div>

            <div className="border-t border-line pt-3">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted mb-2">Coding <span className="text-accent normal-case tracking-normal">· auto-applied, edit if wrong</span></div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-[11px] text-text-muted mb-1">Type</label>
                  <div className="inline-flex border border-line rounded-md bg-ink-raised p-0.5 gap-0.5 w-full">
                    {(["project", "opex", "capex"] as const).map((t) => (
                      <button key={t} type="button" onClick={() => { setInvoiceType(t); setBudgetCode(""); }}
                        className={`flex-1 px-2 py-1 rounded text-[11px] capitalize ${invoiceType === t ? "bg-accent text-white" : "text-text-muted"}`}>{t}</button>
                    ))}
                  </div>
                </div>
                {invoiceType === "project" ? (
                  <div>
                    <label className="block text-[11px] text-text-muted mb-1">Project</label>
                    <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                      <option value="">— select —</option>
                      {projects.map((p: any) => <option key={p.project_id} value={p.project_id}>{[p.project_code, p.project_name, p.site?.site_name].filter(Boolean).join(" · ")}</option>)}
                    </Select>
                  </div>
                ) : <div />}
                <div className="col-span-2">
                  {/* 28 Oct 2026 — the PO and the bucket, with what's left on each */}
                  <BudgetPoPanel
                    projectId={invoiceType === "project" ? projectId : null}
                    supplierId={supplierId}
                    invoiceType={invoiceType as any}
                    codes={budgetCodes as any}
                    budgetCode={budgetCode} onBudgetCode={setBudgetCode}
                    poId={poId} onPo={setPoId}
                    amountEx={Number(amountExGst) || 0}
                    compact
                  />
                </div>
                {(/FUEL/i.test(budgetCode) || manualFuel) ? (
                  <div>
                    <label className="block text-[11px] text-text-muted mb-1">Fuel tax credit</label>
                    <Select value={ftcClass} onChange={(e) => setFtcClass(e.target.value)}>
                      <option value="">— pick —</option>
                      <option value="FTC_OFFROAD_Plant">Plant / off-road</option>
                      <option value="FTC_HV_OnRoad">Heavy vehicle on-road</option>
                      <option value="FTC0_LightVehicle_OnRoad">Light vehicle</option>
                      <option value="FTC_TANK_MIXED">Bulk / mixed</option>
                    </Select>
                  </div>
                ) : (
                  <div className="flex items-end">
                    <button type="button" onClick={() => { setManualFuel(true); if (!budgetCode) setBudgetCode("OX12_FUEL"); }} className="text-[11px] text-accent hover:underline">Mark as fuel (claim FTC)</button>
                  </div>
                )}
                <div>
                  <label className="block text-[11px] text-text-muted mb-1">Paid with</label>
                  <Select value={cardId} onChange={(e) => setCardId(e.target.value)}>
                    <option value="">— no card / normal bill —</option>
                    {(apCards as any[]).map((c) => <option key={c.card_id} value={c.card_id}>••••{c.last4} · {c.label}</option>)}
                  </Select>
                </div>
                <div>
                  <label className="block text-[11px] text-text-muted mb-1">Due date</label>
                  <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text" />
                </div>
              </div>

              <input value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Description for Xero — e.g. 63.57L diesel, plant"
                className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[12px] text-text mt-2.5" />

              {brandMatch?.brand_display_name && (
                <div className="mt-2.5 text-[12px] text-text-muted">Brand: <span className="text-text">{brandMatch.brand_display_name}</span></div>
              )}
              {apSelCard && (
                <div className="mt-2 text-[12px]">{apCardIsReimb
                  ? <span className="text-warn">Reimbursement → Bill to {apSelCard.label}</span>
                  : <span className="text-ok">Spend money · {apSelCard.xero_account_name ?? "card account"}</span>}</div>
              )}
            </div>

            {submit.isError && (
              <div className="text-[12px] text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">
                <AlertTriangle size={11} className="inline mr-1" />{(submit.error as any)?.message}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => navigate("/ap")}>Cancel</Button>
              <Button icon={CheckCircle2} onClick={() => submit.mutate()}
                disabled={submit.isPending || (!supplierId && !vendorName.trim()) || !amountExGst || !budgetCode}>
                {submit.isPending ? "Saving…" : "Approve"}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// ApQueueScreen — /ap
// =============================================================================

type Tab = "pending" | "ready" | "in_xero" | "paid" | "all";

const TAB_STATUSES: Record<Tab, string[] | null> = {
  pending: ["captured", "approved"],
  ready:   ["approved"],
  in_xero: ["entered_in_xero"],
  paid:    ["paid"],
  all:     null,
};

export function ApQueueScreen() {
  const user = useRequireAuth();
  const [tab, setTab] = useState<Tab>("pending");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: kpi } = useQuery({
    queryKey: ["ap_kpi"],
    queryFn: async () => {
      const { data } = await supabase
        .from("ap_invoices")
        .select("ap_invoice_id, status, amount_inc_gst, due_date, paid_date, xero_entry_type, is_reimbursement, xero_bill_id");
      const rows = (data ?? []) as any[];
      const today = new Date().toISOString().slice(0, 10);
      const monthStart = today.slice(0, 7) + "-01";
      return {
        pending_count: rows.filter((r) => ["captured", "approved"].includes(r.status)).length,
        pending_value: rows.filter((r) => ["captured", "approved"].includes(r.status))
          .reduce((a, r) => a + Number(r.amount_inc_gst ?? 0), 0),
        in_xero_value: rows.filter((r) => r.status === "entered_in_xero")
          .reduce((a, r) => a + Number(r.amount_inc_gst ?? 0), 0),
        in_xero_count: rows.filter((r) => r.status === "entered_in_xero").length,
        // The "Bills to pay" button beside this one opens a screen that lists
        // approved bills too (T-483) and that hides un-bundled reimbursements
        // (G4), and counts one Xero bill once however many receipts are on it.
        // Counting entered_in_xero rows disagreed with all three.
        bills_owing_count: new Set(
          rows
            .filter((r) =>
              r.xero_entry_type === "bill"
              && ["approved", "entered_in_xero"].includes(r.status)
              && (!r.is_reimbursement || !!r.xero_bill_id))
            .map((r) => r.xero_bill_id ?? r.ap_invoice_id)
        ).size,
        paid_mtd: rows.filter((r) => r.status === "paid" && r.paid_date && r.paid_date >= monthStart)
          .reduce((a, r) => a + Number(r.amount_inc_gst ?? 0), 0),
        overdue_count: rows.filter((r) =>
          r.status !== "paid" && r.status !== "void" && r.due_date && r.due_date < today
        ).length,
      };
    },
  });

  const { data: invoices = [], isLoading, error } = useQuery({
    queryKey: ["ap_invoices", tab, search],
    queryFn: async () => {
      let q = supabase
        .from("ap_invoices")
        .select(`
          ap_invoice_id, invoice_number, supplier_id, supplier_name,
          invoice_date, due_date, amount_inc_gst, amount_ex_gst,
          status, budget_code, project_id, captured_at, capture_source,
          pdf_storage_path, pdf_original_filename, paid_date,
          xero_bill_number, description
        `)
        .order("captured_at", { ascending: false })
        .limit(300);

      const filter = TAB_STATUSES[tab];
      if (filter) q = q.in("status", filter);
      if (search.trim()) {
        const s = search.trim();
        const clauses = [
          `invoice_number.ilike.%${s}%`,
          `supplier_name.ilike.%${s}%`,
          `description.ilike.%${s}%`,
        ];
        // T-353 — search by total too (for matching a Xero bank line where you
        // only have the amount). Exact match on inc-GST or ex-GST.
        const numStr = s.replace(/[^0-9.]/g, "");
        const num = Number(numStr);
        if (numStr !== "" && !Number.isNaN(num)) {
          clauses.push(`amount_inc_gst.eq.${num}`);
          clauses.push(`amount_ex_gst.eq.${num}`);
        }
        q = q.or(clauses.join(","));
      }

      const { data, error } = await q;
      if (error) throw error;

      // Hydrate project codes in one follow-up query (avoids brittle nested selects)
      const rows = (data ?? []) as any[];
      const projectIds = Array.from(new Set(rows.map((r) => r.project_id).filter(Boolean)));
      if (projectIds.length === 0) return rows;
      const { data: projects } = await supabase
        .from("projects")
        .select("project_id, project_code")
        .in("project_id", projectIds);
      const byId = new Map((projects ?? []).map((p: any) => [p.project_id, p.project_code]));
      return rows.map((r) => ({ ...r, project_code: byId.get(r.project_id) ?? null }));
    },
  });

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: "pending", label: "Pending",      count: kpi?.pending_count },
    { key: "ready",   label: "Ready for Xero" },
    { key: "in_xero", label: "In Xero" },
    { key: "paid",    label: "Paid" },
    { key: "all",     label: "All" },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <Link to="/finance" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Finance hub
      </Link>
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Accounts Payable</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Supplier invoices pending entry, approval, and payment. Anyone can capture — manager+ approves.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={Download} disabled={invoices.length === 0}
            onClick={() => exportCsv(dated(`ap_${tab}`), invoices, [
              { header: "Invoice #",      value: (r: any) => r.invoice_number },
              { header: "Supplier",       value: (r: any) => r.supplier_name },
              { header: "Project",        value: (r: any) => r.project_code },
              { header: "Budget code",    value: (r: any) => r.budget_code },
              { header: "Amount ex GST",  value: (r: any) => r.amount_ex_gst },
              { header: "Amount inc GST", value: (r: any) => r.amount_inc_gst },
              { header: "Invoice date",   value: (r: any) => r.invoice_date },
              { header: "Due date",       value: (r: any) => r.due_date },
              { header: "Captured",       value: (r: any) => (r.captured_at ?? "").slice(0, 10) },
              { header: "Status",         value: (r: any) => r.status },
              { header: "Xero bill #",    value: (r: any) => r.xero_bill_number },
              { header: "Description",    value: (r: any) => r.description },
            ])}>
            Export CSV
          </Button>
          <Link to="/ap/receipts">
            <Button variant="secondary" icon={Receipt}>Pending receipts</Button>
          </Link>
          <Link to="/ap/bills">
            <Button variant="secondary" icon={Banknote}>Bills to pay{kpi?.bills_owing_count ? ` (${kpi.bills_owing_count})` : ""}</Button>
          </Link>
          <Link to="/ap/reconcile">
            <Button variant="secondary" icon={Zap}>Bank rec</Button>
          </Link>
          <Link to="/ap/new">
            <Button icon={Plus}>New invoice</Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <KPI label="Pending"     value={kpi?.pending_count ?? 0} icon={Receipt} accent />
        <KPI label="Pending $"   value={fmtMoney(kpi?.pending_value ?? 0)} />
        <KPI label="In Xero $"   value={fmtMoney(kpi?.in_xero_value ?? 0)} />
        <KPI label="Paid MTD $"  value={fmtMoney(kpi?.paid_mtd ?? 0)} />
        <KPI label="Overdue"     value={kpi?.overdue_count ?? 0} />
      </div>

      <div className="flex items-center gap-1 border-b border-line mb-4 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-[13px] whitespace-nowrap ${
              tab === t.key ? "text-accent border-b-2 border-accent font-semibold" : "text-text-muted border-b-2 border-transparent hover:text-text"
            }`}
          >
            {t.label}
            {typeof t.count === "number" && t.count > 0 && (
              <span className={`ml-1.5 font-mono text-[10px] ${tab === t.key ? "text-accent" : "text-text-muted"}`}>
                ({t.count})
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 max-w-md">
          <Search size={14} className="text-text-muted" />
          <input
            placeholder="Search invoice #, supplier, description, or $ amount…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent outline-none text-text text-[13px] flex-1"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger">
          <AlertTriangle size={12} className="inline mr-1.5" />
          {(error as any).message}
        </div>
      )}

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : invoices.length === 0 ? (
        <Card>
          <EmptyState
            title="No invoices"
            description={tab === "pending" ? "Nothing sitting in the queue — capture one to get started." : "Nothing here for this tab."}
            icon={Receipt}
          />
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line">
                  {["Invoice #", "Supplier", "Project", "Amount", "Captured", "Due", "Status"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv: any) => {
                  const overdue = inv.due_date && inv.status !== "paid" && inv.status !== "void" && inv.due_date < new Date().toISOString().slice(0, 10);
                  return (
                    <tr
                      key={inv.ap_invoice_id}
                      onClick={() => setSelectedId(inv.ap_invoice_id)}
                      className="border-b border-line hover:bg-ink-hover cursor-pointer"
                    >
                      <td className="px-4 py-3 font-mono text-[11px] text-accent">{inv.invoice_number ?? "—"}</td>
                      <td className="px-4 py-3">{inv.supplier_name ?? "—"}</td>
                      <td className="px-4 py-3 font-mono text-[11px] text-text-muted">{inv.project_code ?? "—"}</td>
                      <td className="px-4 py-3 font-mono">{fmtMoney(inv.amount_inc_gst)}</td>
                      <td className="px-4 py-3 font-mono text-[11px] text-text-muted">
                        {fmtDate(inv.captured_at)}
                        {inv.capture_source === "email" && <Mail size={10} className="inline ml-1.5 text-text-dim" />}
                      </td>
                      <td className={`px-4 py-3 font-mono text-[11px] ${overdue ? "text-danger" : "text-text-muted"}`}>
                        {fmtDate(inv.due_date)}
                        {overdue && <span className="ml-1">⚠</span>}
                      </td>
                      <td className="px-4 py-3">{statusPill(inv.status)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Drawer */}
      {selectedId && (
        <ApInvoiceDrawer
          apInvoiceId={selectedId}
          onClose={() => setSelectedId(null)}
          canEdit={hasRole(user, "manager")}
        />
      )}
    </div>
  );
}

// =============================================================================
// ApInvoiceDrawer — slide-out detail + state transition actions
// =============================================================================

function ApInvoiceDrawer({
  apInvoiceId,
  onClose,
  canEdit,
}: {
  apInvoiceId: string;
  onClose: () => void;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [xeroBillNumber, setXeroBillNumber] = useState("");
  const [xeroBillId, setXeroBillId] = useState("");
  // Track if user has manually typed something so we don't overwrite their edit.
  const [xeroBillNumberDirty, setXeroBillNumberDirty] = useState(false);
  const [paymentRef, setPaymentRef] = useState("");
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState<"view" | "xero" | "paid">("view");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const { data: inv, isLoading } = useQuery({
    queryKey: ["ap_invoice", apInvoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ap_invoices")
        .select("*")
        .eq("ap_invoice_id", apInvoiceId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      const [sRes, pRes, bcRes, cbRes] = await Promise.all([
        data.supplier_id
          ? supabase.from("suppliers").select("legal_name, abn, payment_terms_days, primary_contact_name, primary_contact_email").eq("supplier_id", data.supplier_id).maybeSingle()
          : Promise.resolve({ data: null }),
        data.project_id
          ? supabase.from("projects").select("project_code, project_name").eq("project_id", data.project_id).maybeSingle()
          : Promise.resolve({ data: null }),
        data.budget_code
          ? supabase.from("budget_codes").select("xero_account_code, xero_tracking_budget, xero_tracking_ftc").eq("budget_code", data.budget_code).maybeSingle()
          : Promise.resolve({ data: null }),
        // Who loaded it — captured_by is a users.user_id; resolve to the person.
        data.captured_by
          ? supabase.from("users").select("login_email, employees:employee_id ( first_name, last_name )").eq("user_id", data.captured_by).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      const cb: any = (cbRes as any).data;
      const capturedByName = cb
        ? ([cb.employees?.first_name, cb.employees?.last_name].filter(Boolean).join(" ") || cb.login_email || null)
        : null;
      return { ...data, supplier: sRes.data, project: pRes.data, xero_map: bcRes.data, captured_by_name: capturedByName } as any;
    },
  });

  // Generate signed URL for PDF viewing (valid for 1hr).
  // ALL receipts (mobile + bulk PDF intake) land in receipt-captures bucket;
  // the legacy 'ap-documents' bucket reference was wrong.
  useEffect(() => {
    if (!inv?.pdf_storage_path) { setPdfUrl(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.storage
        .from("receipt-captures")
        .createSignedUrl(inv.pdf_storage_path, 3600);
      if (!cancelled) setPdfUrl(data?.signedUrl ?? null);
    })();
    return () => { cancelled = true; };
  }, [inv?.pdf_storage_path]);

  const setStatus = useMutation({
    mutationFn: async (patch: any) => {
      const { error } = await supabase
        .from("ap_invoices")
        .update(patch)
        .eq("ap_invoice_id", apInvoiceId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ap_invoices"] });
      qc.invalidateQueries({ queryKey: ["ap_invoice", apInvoiceId] });
      qc.invalidateQueries({ queryKey: ["ap_kpi"] });
      setMode("view");
    },
  });

  if (!inv && !isLoading) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />
      <aside className="fixed right-0 top-0 bottom-0 w-full md:w-[520px] bg-ink-card border-l border-line z-50 overflow-y-auto">
        {isLoading || !inv ? (
          <div className="flex justify-center py-20"><Spinner /></div>
        ) : (
          <>
            <div className="sticky top-0 bg-ink-card border-b border-line px-5 py-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-mono text-[13px] text-accent truncate">
                  {inv.invoice_number ?? "(no invoice #)"}
                </div>
                <div className="text-[13px] text-text truncate">{inv.supplier_name ?? "—"}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {statusPill(inv.status)}
                <button onClick={onClose} className="text-text-muted hover:text-text p-1">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase text-text-muted font-mono">Ex GST</div>
                  <div className="text-[15px] font-mono text-text mt-0.5">{fmtMoney(inv.amount_ex_gst)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-text-muted font-mono">GST</div>
                  <div className="text-[15px] font-mono text-text-muted mt-0.5">{fmtMoney(inv.gst_amount)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-text-muted font-mono">Inc GST</div>
                  <div className="text-[15px] font-mono text-accent mt-0.5">{fmtMoney(inv.amount_inc_gst)}</div>
                </div>
              </div>

              <div className="border border-line rounded-lg p-3 space-y-2 text-[12px]">
                <Row label="Invoice date" value={fmtDate(inv.invoice_date)} />
                <Row label="Due date" value={fmtDate(inv.due_date)} />
                <Row label="Budget code" value={inv.budget_code ?? "—"} mono />
                <Row label="Project" value={inv.project?.project_code ? `${inv.project.project_code} — ${inv.project.project_name}` : "—"} />
                <Row label="Description" value={inv.description ?? "—"} />
                <Row label="Captured" value={`${fmtDate(inv.captured_at)} (${inv.capture_source})${inv.captured_by_name ? ` by ${inv.captured_by_name}` : ""}`} />
                {(inv.card_last4 || inv.payment_method) && (
                  <Row
                    label="Paid via"
                    value={
                      inv.card_last4
                        ? `Card ****${inv.card_last4}${inv.payment_method ? ` (${inv.payment_method})` : ""}`
                        : (inv.payment_method ?? "—")
                    }
                    mono
                  />
                )}
                {/* Xero Reference — one-click copy. Pattern: {InvNo} | {BudgetCode} | {Project or OPEX}
                    Budget code in the middle so Xero ref column scans cleanly:
                    "0223740081 | 5022-FUEL-LV | OPEX" tells you at a glance what it is. */}
                <div className="flex items-center justify-between border-t border-line/40 pt-2 mt-1">
                  <span className="text-text-muted">Xero ref</span>
                  {(() => {
                    const ref = `${inv.invoice_number ?? "NO-REF"} | ${inv.budget_code ?? "NO-CODE"} | ${inv.project?.project_code ?? "OPEX"}`;
                    return (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(ref);
                          } catch {
                            // Fallback for old browsers
                            const ta = document.createElement("textarea");
                            ta.value = ref;
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand("copy");
                            document.body.removeChild(ta);
                          }
                        }}
                        className="font-mono text-[11px] text-accent hover:bg-accent/10 px-2 py-0.5 rounded border border-accent/30 inline-flex items-center gap-1.5"
                        title="Copy to clipboard for pasting into Xero Reference field"
                      >
                        <Copy size={10}/> {ref}
                      </button>
                    );
                  })()}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Xero entry type</span>
                  {canEdit && inv.status !== "paid" && inv.status !== "void" ? (
                    <select
                      value={inv.xero_entry_type ?? "spend_money"}
                      onChange={(e) => setStatus.mutate({ xero_entry_type: e.target.value })}
                      className="bg-ink-raised border border-line rounded px-2 py-0.5 text-[11px] font-mono text-text"
                    >
                      <option value="spend_money">Spend Money (paid on card)</option>
                      <option value="bill">Bill (supplier invoice on terms)</option>
                    </select>
                  ) : (
                    <span className="font-mono text-[11px] text-text">{inv.xero_entry_type === "bill" ? "Bill" : "Spend Money"}</span>
                  )}
                </div>
                {inv.approved_at && <Row label="Approved" value={fmtDate(inv.approved_at)} />}
                {inv.entered_in_xero_at && <Row label="Xero bill #" value={inv.xero_bill_number ?? "(entered)"} mono />}
                {inv.paid_date && <Row label="Paid" value={`${fmtDate(inv.paid_date)} · ${inv.payment_reference ?? "no ref"}`} mono />}
              </div>

              {inv.notes && (
                <div className="border border-line rounded-lg p-3 text-[12px]">
                  <div className="text-[10px] uppercase text-text-muted font-mono mb-1">Notes</div>
                  {inv.notes}
                </div>
              )}

              {inv.pdf_storage_path ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={!pdfUrl}
                    onClick={async () => {
                      if (!pdfUrl) return;
                      // Force-download via fetch+blob — bypasses cross-origin
                      // restrictions on the `download` attribute that R2 signed
                      // URLs hit. Works in all modern browsers.
                      try {
                        const res = await fetch(pdfUrl);
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = inv.pdf_original_filename ?? `${inv.supplier_name ?? "receipt"}-${inv.ap_invoice_id?.slice(0, 8)}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      } catch (e: any) {
                        window.alert(`Download failed: ${e?.message ?? "unknown"}`);
                      }
                    }}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-accent/10 border border-accent/40 rounded hover:border-accent text-[12px] text-accent disabled:opacity-50"
                  >
                    <Download size={12} /> Download PDF
                  </button>
                  <a
                    href={pdfUrl ?? "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-ink-raised border border-line rounded hover:border-accent/40 text-[12px] text-text-muted"
                  >
                    <FileText size={12} /> View
                  </a>
                </div>
              ) : (
                <div className="px-3 py-2 bg-ink-raised border border-line rounded text-[12px] text-text-muted">
                  No file attached
                </div>
              )}

              {/* T-488d — once it has been pushed, the transaction EXISTS in Xero
                  and we hold its GUID, so link straight to it. The old buttons
                  opened a blank new-bill form, which after a push is worse than
                  no button: it invites keying the same spend in a second time. */}
              {xeroTransactionUrl(inv.xero_entry_type, inv.xero_bill_id) && (
                <a
                  href={xeroTransactionUrl(inv.xero_entry_type, inv.xero_bill_id)!}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-ink-raised border border-line rounded hover:border-accent/40 text-[12px] text-text"
                >
                  <ExternalLink size={12} />
                  View {inv.xero_entry_type === "bill" ? "bill" : "spend money"} in Xero
                </a>
              )}

              {/* Not pushed (or pushed before we recorded ids) — the manual path. */}
              {!xeroTransactionUrl(inv.xero_entry_type, inv.xero_bill_id)
                && canEdit && inv.status !== "paid" && inv.status !== "void" && (
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href="https://go.xero.com/AccountsPayable/Edit.aspx?invoiceID=NEW"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-ink-raised border border-line rounded hover:border-accent/40 text-[12px] text-text"
                  >
                    <ExternalLink size={12} /> Open as Bill in Xero
                  </a>
                  <a
                    href="https://go.xero.com/Bank/BankAccounts.aspx"
                    target="_blank"
                    rel="noreferrer"
                    title="Open Xero bank account list — for Spend Money, click the right account's Reconcile and Create from the bank line itself (avoids wrong-account Spend Money mess)."
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-ink-raised border border-line rounded hover:border-accent/40 text-[12px] text-text"
                  >
                    <ExternalLink size={12} /> Open Xero Bank Rec
                  </a>
                </div>
              )}

              {/* Action area */}
              {canEdit && inv.status !== "paid" && inv.status !== "void" && (
                <div className="border-t border-line pt-4 space-y-2">
                  <div className="text-[10px] uppercase text-text-muted font-mono font-semibold">Actions</div>

                  {mode === "view" && (
                    <div className="flex flex-wrap gap-2">
                      {inv.status === "captured" && (
                        <Button icon={Check} onClick={() => setStatus.mutate({ status: "approved", approved_at: new Date().toISOString() })}>
                          Approve
                        </Button>
                      )}
                      {(inv.status === "captured" || inv.status === "approved") && (
                        <Button variant="secondary" icon={Send} onClick={() => {
                          // Auto-prefill the Xero ref so user just confirms (or tweaks)
                          // — same string the Copy button at the top of the drawer builds.
                          if (!xeroBillNumberDirty) {
                            const ref = `${inv.invoice_number ?? "NO-REF"} | ${inv.project?.project_code ?? "OPEX"}`;
                            setXeroBillNumber(ref);
                          }
                          setMode("xero");
                        }}>
                          {inv.xero_entry_type === "bill"
                            ? "Mark entered in Xero (Bill)"
                            : "Mark reconciled in Xero (Spend Money → Paid)"}
                        </Button>
                      )}
                      {inv.status === "entered_in_xero" && (
                        <Button icon={DollarSign} onClick={() => setMode("paid")}>
                          Mark paid
                        </Button>
                      )}
                      {inv.status === "approved" && (
                        <Button variant="secondary" icon={RotateCcw} onClick={() => setStatus.mutate({ status: "captured", approved_at: null, approved_by: null })}>
                          Revert
                        </Button>
                      )}
                      {/* T-498 — a way back from "In Xero". Ticking Entered by
                          mistake used to be a one-way door: the bill vanished
                          from Bills to Pay and could only be fixed with SQL.
                          If it was pushed through the API the transaction really
                          IS in Xero, so that case says so plainly rather than
                          pretending our status is the whole story. */}
                      {inv.status === "entered_in_xero" && (
                        <Button variant="secondary" icon={RotateCcw}
                          title="Put this back in Bills to Pay"
                          onClick={() => {
                            const pushed = !!inv.xero_bill_id;
                            const msg = pushed
                              ? `This was pushed to Xero and the transaction exists there (${inv.xero_bill_number ?? inv.xero_bill_id}).\n\n` +
                                `Moving it back here does NOT remove it from Xero — delete or void it in Xero first, ` +
                                `or you will end up paying it twice.\n\nPut it back in Bills to Pay anyway?`
                              : `Move this back to approved so it shows in Bills to Pay again?`;
                            if (!window.confirm(msg)) return;
                            setStatus.mutate({
                              status:             "approved",
                              entered_in_xero_at: null,
                              entered_in_xero_by: null,
                              // Clear the link only when nothing was actually posted.
                              ...(pushed ? {} : { xero_bill_number: null, xero_bill_id: null, xero_pushed_at: null }),
                            });
                          }}>
                          Back to bills to pay
                        </Button>
                      )}
                      <Button variant="secondary" icon={Ban} onClick={() => setStatus.mutate({ status: "void" })}>
                        Void
                      </Button>
                    </div>
                  )}

                  {mode === "xero" && (
                    <div className="border border-accent/40 rounded-lg p-3 bg-accent/5 space-y-2">
                      <div className="text-[11px] uppercase text-accent font-semibold tracking-wider2">
                        {inv.xero_entry_type === "bill"
                          ? "Record Xero bill reference"
                          : "Record Xero Spend Money reference (will mark as paid)"}
                      </div>
                      <Field label={inv.xero_entry_type === "bill" ? "Xero bill number" : "Xero reference / bank txn"} hint="Auto-prefilled to match the Xero ref you copied — edit if Xero numbered the entry differently">
                        <Input value={xeroBillNumber} onChange={(e) => { setXeroBillNumber(e.target.value); setXeroBillNumberDirty(true); }} mono placeholder={inv.xero_entry_type === "bill" ? "e.g. BILL-0042" : "e.g. EFTPOS 25/04 Caltex"} />
                      </Field>
                      {inv.xero_entry_type === "bill" && (
                        <Field label="Xero bill ID (optional)" hint="Internal Xero UUID — paste from URL if you want">
                          <Input value={xeroBillId} onChange={(e) => setXeroBillId(e.target.value)} mono />
                        </Field>
                      )}
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setMode("view")}>Cancel</Button>
                        <Button icon={Check} onClick={() => {
                          // Spend Money in Xero is created+reconciled in one step → flip
                          // straight to paid. Bill goes to entered_in_xero, awaiting payment.
                          if (inv.xero_entry_type === "bill") {
                            setStatus.mutate({
                              status: "entered_in_xero",
                              xero_bill_number: xeroBillNumber.trim() || null,
                              xero_bill_id: xeroBillId.trim() || null,
                              entered_in_xero_at: new Date().toISOString(),
                            });
                          } else {
                            // spend_money path
                            setStatus.mutate({
                              status: "paid",
                              xero_bill_number: xeroBillNumber.trim() || null,  // store the Xero ref
                              entered_in_xero_at: new Date().toISOString(),
                              paid_date: new Date().toISOString().slice(0, 10),
                              paid_amount: inv.amount_inc_gst,
                              payment_reference: xeroBillNumber.trim() || null,
                            });
                          }
                        }}>
                          {inv.xero_entry_type === "bill" ? "Confirm" : "Confirm + mark paid"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {mode === "paid" && (
                    <div className="border border-accent/40 rounded-lg p-3 bg-accent/5 space-y-2">
                      <div className="text-[11px] uppercase text-accent font-semibold tracking-wider2">
                        Mark as paid
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Paid date">
                          <Input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
                        </Field>
                        <Field label="Payment ref">
                          <Input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} mono placeholder="Bank ref / txn id" />
                        </Field>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setMode("view")}>Cancel</Button>
                        <Button icon={Check} onClick={() => setStatus.mutate({
                          status: "paid",
                          paid_date: paidDate,
                          paid_amount: inv.amount_inc_gst,
                          payment_reference: paymentRef.trim() || null,
                        })}>
                          Confirm paid
                        </Button>
                      </div>
                    </div>
                  )}

                  {setStatus.isError && (
                    <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">
                      <AlertTriangle size={11} className="inline mr-1" />
                      {(setStatus.error as any)?.message}
                    </div>
                  )}
                </div>
              )}

              {!canEdit && (
                <div className="border-t border-line pt-4 text-[11px] text-text-muted">
                  Only managers + directors can transition invoice status.
                </div>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2">
      <div className="text-text-muted">{label}</div>
      <div className={mono ? "font-mono" : ""}>{value}</div>
    </div>
  );
}
